import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useActiveCompanyRole } from '@hooks/usePermission';

import { Dropdown, filterBarSelectStyles } from '@components/Dropdown';
import { SingleDatePicker } from '@components/SingleDatePicker';
import { addNotification, notificationStates } from '@components/Notification';
import { useNavbarSlot } from '@/contexts/NavbarSlot.context';

import { useTeamAttendanceSettings } from '@/hooks/useTeamAttendanceSettings';
import teamService from '@/services/team';
import alertsService from '@/services/alerts';
import lateEarlyAlertsService from '@/services/lateEarlyAlerts';

import { StatsRow } from './components/StatsRow.component';
import {
  CheckInTrendChart,
  CheckInTimeDistribution,
} from './components/CheckInCharts.component';
import { ExceptionsTable } from './components/ExceptionsTable.component';
import {
  RecentAlertsPanel,
  AttendancePolicyPanel,
} from './components/SidePanels.component';

import styles from './LateEarlyLoginAlerts.module.scss';

export const LateEarlyLoginAlertsComponent = () => {
  const userProfile = useSelector((state) => state.auth?.profile);
  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const role = useActiveCompanyRole();
  const activeTeamId = useSelector((state) => state.team?.active?.activeTeamid);

  const [teamOptions, setTeamOptions] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  // null until a team's timezone loads — the dateId memo below falls back
  // to computing "today" once the team's own timezone is known, rather
  // than guessing from the browser's local date.
  const [selectedDate, setSelectedDate] = useState(null);

  const [rollups, setRollups] = useState([]);
  const [loadingRollups, setLoadingRollups] = useState(true);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [memberDirectory, setMemberDirectory] = useState({});

  const teamId = selectedTeam?.value;
  const {
    alertConfig,
    workingSchedule,
    timeZone,
    isConfigured,
    loading: loadingSettings,
  } = useTeamAttendanceSettings(teamId);

  // Default the date picker to "today" in the selected team's own timezone
  // once it's known — resets on team switch, but doesn't fight a manual
  // date-picker selection within the same team.
  useEffect(() => {
    if (teamId)
      setSelectedDate(lateEarlyAlertsService.getTodayDateId(timeZone));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  // Load the teams this user can view — same call as Team Settings.
  useEffect(() => {
    let cancelled = false;
    const fetchTeams = async () => {
      if (!companyId || !userId) return;
      setLoadingTeams(true);
      const res = await teamService.getMemberTeamCollection({
        companyId,
        userId,
        role,
        companyRole: role,
      });
      if (cancelled) return;
      if (res.success) {
        const options = res.data.map((team) => ({
          value: team.docId,
          label: team.Team,
        }));
        setTeamOptions(options);
        setSelectedTeam(
          options.find((option) => option.value === activeTeamId) ||
            options[0] ||
            null
        );
      } else {
        addNotification({
          message: 'Failed to load teams. Please try again.',
          level: notificationStates.error,
        });
      }
      setLoadingTeams(false);
    };
    fetchTeams();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userId, role]);

  // dateId anchors to the selected date, always resolved via the team's own
  // timezone — not the browser's — so "today" means the same thing here as
  // it does in the backend trigger that produced this data.
  const dateId = useMemo(
    () => selectedDate || lateEarlyAlertsService.getTodayDateId(timeZone),
    [selectedDate, timeZone]
  );
  const last7DateIds = useMemo(
    () => lateEarlyAlertsService.getLastNDateIds(dateId, timeZone, 7),
    [dateId, timeZone]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!teamId) return;
      setLoadingRollups(true);
      const res = await lateEarlyAlertsService.getTeamRollupRange(
        teamId,
        last7DateIds
      );
      if (cancelled) return;
      setRollups(res.data);
      setLoadingRollups(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [teamId, last7DateIds]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!teamId) return;
      setLoadingAlerts(true);
      const [alertsRes, directoryRes] = await Promise.all([
        alertsService.getTeamRecentAlerts(teamId, { limit: 5 }),
        lateEarlyAlertsService.getTeamMemberDirectory(teamId),
      ]);
      if (cancelled) return;
      setRecentAlerts(alertsRes.data);
      setMemberDirectory(directoryRes.data);
      setLoadingAlerts(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const todayRollup = rollups[rollups.length - 1] || {};

  // Date + Department pickers live in the shared navbar slot, matching
  // DashboardOverviewNew/TeamDashboard's convention — every other page with
  // these filters injects into the one existing navbar control, not a
  // second page-local copy of the same pickers.
  const setNavbarSlot = useNavbarSlot();
  useEffect(() => {
    setNavbarSlot(
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '220px' }}>
          <Dropdown
            options={teamOptions}
            value={selectedTeam}
            onChange={setSelectedTeam}
            isLoading={loadingTeams}
            isDisabled={loadingTeams}
            placeholder="Select department..."
            selectStyles={filterBarSelectStyles}
          />
        </div>
        <SingleDatePicker
          date={selectedDate}
          onDateChange={setSelectedDate}
          disableFutureDates
        />
      </div>
    );
    return () => setNavbarSlot(null);
  }, [teamOptions, selectedTeam, loadingTeams, selectedDate, setNavbarSlot]);

  return (
    <div className={styles.page}>
      {!teamId ? (
        <div className={styles.emptyState}>
          {loadingTeams
            ? 'Loading teams…'
            : 'Select a team to view its attendance alerts.'}
        </div>
      ) : (
        <>
          <StatsRow rollups={rollups} isLoading={loadingRollups} />

          <div className={styles.chartsRow}>
            <CheckInTrendChart rollups={rollups} isLoading={loadingRollups} />
            <CheckInTimeDistribution
              rollup={todayRollup}
              isLoading={loadingRollups}
            />
          </div>

          <div className={styles.bottomRow}>
            <ExceptionsTable
              teamId={teamId}
              dateId={dateId}
              timeZone={timeZone}
              lateCount={Number(todayRollup.lateCount) || 0}
              earlyCount={Number(todayRollup.earlyCount) || 0}
              memberDirectory={memberDirectory}
            />
            <div className={styles.sideColumn}>
              <RecentAlertsPanel
                alerts={recentAlerts}
                memberDirectory={memberDirectory}
                timeZone={timeZone}
                isLoading={loadingAlerts}
              />
              <AttendancePolicyPanel
                alertConfig={alertConfig}
                workingSchedule={workingSchedule}
                isConfigured={isConfigured}
                isLoading={loadingSettings}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

LateEarlyLoginAlertsComponent.displayName = 'LateEarlyLoginAlerts';
